/** @format */

import {
	AthleteManagementIdentityService,
	IdentityService
} from '@my-account/data-access/services';
import {
	AthleteProfile,
	Identity,
	IdentityV2,
	LoyaltyInformation
} from '@my-account/data-access/interfaces';
import { Environment, PersonalInformation, Phone } from '@shared/data-access/interfaces';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AthleteManagementDao } from '@my-account/data-access/dao';
import { HttpService, MyAccountAnalyticsService, Store } from '@shared/data-access/services';

import { Injectable } from '@angular/core';

import { MyAccountErrorLogKeys } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class MyAccountService {
	personalInfo: PersonalInformation = null;
	isLoading = false;
	failedToGetLoyaltyInfo = 'Failed to get Loyalty Info';

	private loyaltyInformation: LoyaltyInformation = {
		isGold: false,
		tierExpiration: undefined,
		currentYearSales: 0,
		enableAvailableOffers: false
	};

	constructor(
		private environment: Environment,
		private httpService: HttpService,
		private identityService: IdentityService,
		private athleteManagementIdentityService: AthleteManagementIdentityService,
		private athleteManagementDao: AthleteManagementDao,
		private store: Store,
		private myAccountAnalyticsService: MyAccountAnalyticsService
	) {}

	public getPersonalInformation(includePhones = false, includeAddresses = false) {
		return this.identityService.getIdentity(includePhones, includeAddresses).pipe(
			concatMap((result) =>
				this.getLoyaltyInformation().pipe(
					map((loyaltyInfo) => this.setPersonalInfo(result, loyaltyInfo)),
					catchError(() => {
						this.myAccountAnalyticsService.generateLogError(
							MyAccountErrorLogKeys.MYACCOUNT_LOYALTY_INFO,
							this.failedToGetLoyaltyInfo
						);
						return of(this.setPersonalInfo(result, this.loyaltyInformation));
					})
				)
			)
		);
	}

	public getPersonalInformationV2(timeoutEnabled = false) {
		return this.athleteManagementIdentityService.getIdentity(timeoutEnabled).pipe(
			concatMap((result) =>
				this.getLoyaltyInformation().pipe(
					map((loyaltyInfo) => this.setPersonalInfoV2(result, loyaltyInfo)),
					catchError(() => {
						this.myAccountAnalyticsService.generateLogError(
							MyAccountErrorLogKeys.MYACCOUNT_LOYALTY_INFO,
							this.failedToGetLoyaltyInfo
						);
						return of(this.setPersonalInfoV2(result, this.loyaltyInformation));
					})
				)
			)
		);
	}

	public getPersonalInformationV3() {
		return this.athleteManagementIdentityService.getIdentity().pipe(
			concatMap((result) =>
				this.getLoyaltyInformation().pipe(
					map((loyaltyInfo) => this.setPersonalInfoV3(result, loyaltyInfo)),
					catchError(() => {
						this.myAccountAnalyticsService.generateLogError(
							MyAccountErrorLogKeys.MYACCOUNT_LOYALTY_INFO,
							this.failedToGetLoyaltyInfo
						);
						return of(this.setPersonalInfoError());
					})
				)
			),
			catchError(() => of(this.setPersonalInfoError()))
		);
	}

	public getLoyaltyInformation(): Observable<LoyaltyInformation> {
		const url = `${this.environment.baseUrls.athleteService}/v3/athlete/loyaltyinformation`;
		return this.httpService.get(url, 0, { useApiAuth: true });
	}

	public updateName(firstName: string, lastName: string): Observable<any> {
		const url = `${this.environment.baseUrls.athleteService}/v2/accountsettings/updatename`;

		return this.httpService.post(url, { firstName, lastName }, { useApiAuth: true });
	}

	/**
	 * @returns {Observable<any>}
	 * @description
	 * Updates athlete profile for page 2.
	 * @param athleteProfile
	 * @param shouldIncentive
	 */
	public updateAthleteProfile(
		athleteProfile: AthleteProfile,
		shouldIncentive = false
	): Observable<any> {
		return this.athleteManagementDao.updateAthleteProfile(athleteProfile, shouldIncentive);
	}

	private setPersonalInfo(
		identity: Identity,
		loyaltyInfo: LoyaltyInformation
	): PersonalInformation {
		const personalInfo = new PersonalInformation();
		personalInfo.firstName = identity.data.firstName;
		personalInfo.lastName = identity.data.lastName;
		personalInfo.email = identity.data.emailAddress;
		personalInfo.addresses = identity.data.addresses;
		personalInfo.phoneNumbers =
			identity.data.phoneNumbers.length > 0
				? identity.data.phoneNumbers
				: [
						{
							id: '1',
							phoneNumber: '',
							phoneType: 0,
							isPrimary: false
						}
					];
		personalInfo.loyaltyNumber = identity.data.scorecard;
		personalInfo.isGold = loyaltyInfo.isGold;
		personalInfo.tierExpiration = loyaltyInfo.tierExpiration;
		personalInfo.currentYearSales = loyaltyInfo.currentYearSales;

		this.personalInfo = personalInfo;

		this.store.set('PERSONAL_INFO', personalInfo);

		return personalInfo;
	}

	private setPersonalInfoV2(
		identity: IdentityV2,
		loyaltyInfo: LoyaltyInformation
	): PersonalInformation {
		const personalInfo = new PersonalInformation();
		personalInfo.firstName = identity.firstName;
		personalInfo.lastName = identity.lastName;
		personalInfo.email = identity.email;
		personalInfo.addressesV2 = identity.addresses;
		personalInfo.primaryAddress = identity.primaryAddress;
		const phone: Phone = {
			id: '1',
			phoneNumber: identity.phone,
			phoneType: 0,
			isPrimary: false
		};
		personalInfo.phoneNumbers = [phone];
		personalInfo.loyaltyNumber = identity.scorecard;
		personalInfo.isGold = loyaltyInfo.isGold;
		personalInfo.tierExpiration = loyaltyInfo.tierExpiration;
		personalInfo.currentYearSales = loyaltyInfo.currentYearSales;
		personalInfo.userId = identity.identityId;

		this.personalInfo = personalInfo;

		this.store.set('PERSONAL_INFO', personalInfo);

		return personalInfo;
	}

	private setPersonalInfoV3(
		identity: IdentityV2,
		loyaltyInfo: LoyaltyInformation
	): PersonalInformation {
		const personalInfo = new PersonalInformation();
		personalInfo.firstName = identity.firstName;
		personalInfo.lastName = identity.lastName;
		personalInfo.email = identity.email;
		personalInfo.addressesV2 = identity.addresses;
		personalInfo.primaryAddress = identity.primaryAddress;
		const phone: Phone = {
			id: '',
			isPrimary: false,
			phoneType: 0,
			phoneNumber: identity.phone
		};
		personalInfo.phoneNumbers = [phone];
		personalInfo.loyaltyNumber = identity.scorecard;
		personalInfo.isGold = loyaltyInfo.isGold;
		personalInfo.tierExpiration = loyaltyInfo.tierExpiration;
		personalInfo.currentYearSales = loyaltyInfo.currentYearSales;
		personalInfo.userId = identity.identityId;
		personalInfo.MemberSince = loyaltyInfo['memberSince'];
		personalInfo.GoldSince = loyaltyInfo['goldSince'];
		personalInfo.creditGoldSince = loyaltyInfo['creditGoldSince'];

		this.personalInfo = personalInfo;

		this.store.set('PERSONAL_INFO', personalInfo);

		return personalInfo;
	}

	private setPersonalInfoError(): PersonalInformation {
		const personalInfo = new PersonalInformation();
		personalInfo.error = true;
		this.store.set('PERSONAL_INFO', personalInfo);

		return personalInfo;
	}
}
